import React, { useState } from "react";
import {
    Container, Typography, Paper, TextField, Button,
    Box, Dialog, DialogContent, DialogActions
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import API from "../../api/axiosInstance";
import { useAppTheme } from "../../themes/ThemeContext";

const RequestPasswordReset: React.FC = () => {
    const [email, setEmail] = useState("");
    const [modalVisible, setModalVisible] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const { mode } = useAppTheme();

    const isValidEmail = (email: string) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const handleRequestReset = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        if (!isValidEmail(email)) {
            setError("Please enter a valid email address.");
            return;
        }

        try {
            await API.post("/auth/request-password-reset", { email });
            setModalVisible(true);
        } catch (err: any) {
            setError(
                err?.response?.data?.message ||
                "An error occurred while requesting the reset link."
            );
        }
    };

    const closeModal = () => {
        setModalVisible(false);
        navigate("/login");
    };

    return (
        <Box
            className="d-flex flex-column min-vh-100"
            sx={{
                bgcolor: mode === "dark" ? "#1c1c1c" : "#f5f5f5",
                color: mode === "dark" ? "#f5f5f5" : "#111",
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Header />

            <Container maxWidth="sm" sx={{ mt: 12, mb: 4, flex: 1 }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Typography variant="h5" align="center" gutterBottom>
                        Request Password Reset
                    </Typography>

                    <Typography variant="body1" align="center" sx={{ mb: 3 }}>
                        Use this form to request a link to reset your password.
                    </Typography>

                    <form onSubmit={handleRequestReset}>
                        <Box display="flex" flexDirection="column" gap={2}>
                            <TextField
                                label="Email"
                                type="email"
                                variant="outlined"
                                fullWidth
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            {error && (
                                <Typography color="error" variant="body2" align="center">
                                    {error}
                                </Typography>
                            )}
                            <Button type="submit" variant="contained" color="primary" fullWidth>
                                Submit
                            </Button>
                        </Box>
                    </form>
                </Paper>
            </Container>

            <Footer />

            {/* MUI Modal */}
            <Dialog open={modalVisible} onClose={closeModal}>
                <DialogContent sx={{ textAlign: "center", pt: 4 }}>
                    <Typography variant="h6" gutterBottom>
                        Request Made
                    </Typography>
                    <Typography variant="body1">
                        If you have an account registered with the email you provided, you will receive an email with steps to reset your password.
                        <br /><br />
                        Please make sure to check your spam or junk folders.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
                    <Button onClick={closeModal} variant="contained">
                        Back to Login
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RequestPasswordReset;
